import { supabase } from "../lib/supabase";
import type {
  PmsAvailability,
  PmsDateRangeQuery,
  PmsHotelConfig,
  PmsInfoCollection,
  PmsRate,
} from "../types/pmsInfo";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Read-only client for the PMS gateway endpoints exposed by api-v2-zeswitch.
 * Every call is scoped to a hotel through the `hotel-code` header and is
 * authorised with the caller's Supabase JWT.
 */
class PmsInfoService {
  private async getAuthHeaders(hotelCode: string): Promise<HeadersInit> {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error || !session?.access_token) {
      throw new Error("Your session has expired. Please sign in again.");
    }

    return {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
      "hotel-code": hotelCode,
    };
  }

  private buildQuery(
    params: Record<string, string | number | string[] | undefined>
  ): string {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === "") {
        return;
      }
      if (Array.isArray(value)) {
        if (value.length) {
          search.set(key, value.join(","));
        }
        return;
      }
      search.set(key, String(value));
    });
    const query = search.toString();
    return query ? `?${query}` : "";
  }

  private async request<T>(path: string, hotelCode: string): Promise<T> {
    if (!API_BASE_URL) {
      throw new Error(
        "API base URL is not configured (VITE_API_BASE_URL). Please contact support."
      );
    }

    const headers = await this.getAuthHeaders(hotelCode);
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response));
    }

    return response.json() as Promise<T>;
  }

  private async buildErrorMessage(response: Response): Promise<string> {
    let message = `Request failed: ${response.status} ${response.statusText}`;

    try {
      const body = await response.json();
      message = body?.message || body?.error || message;
    } catch {
      try {
        const text = await response.text();
        if (text) message = text;
      } catch {
        // keep the default message
      }
    }

    if (response.status === 401) {
      return `Authentication failed: ${message}`;
    }
    if (response.status === 403) {
      return `Permission denied: ${message}. The PMS gateway only accepts callers linked to this hotel through an active integration.`;
    }
    if (response.status === 404) {
      return `Not available: ${message}`;
    }

    return message;
  }

  /** Hotel configuration as normalised by the connector (zeswitch format). */
  async getHotelConfig(
    hotelCode: string,
    collections: PmsInfoCollection[] = ["all"]
  ): Promise<PmsHotelConfig> {
    const query = this.buildQuery({ collections });
    return this.request<PmsHotelConfig>(`/v1/hotelConfig${query}`, hotelCode);
  }

  /** Daily rates for a date range. */
  async getRates(hotelCode: string, params: PmsDateRangeQuery): Promise<PmsRate[]> {
    const query = this.buildQuery({
      from: params.from,
      to: params.to,
      room_type_ids: params.roomTypeIds,
      rate_ids: params.rateIds,
      page: params.page,
      pageSize: params.pageSize,
    });
    const data = await this.request<PmsRate[] | { rates?: PmsRate[] }>(
      `/v1/rates${query}`,
      hotelCode
    );
    return Array.isArray(data) ? data : (data?.rates ?? []);
  }

  /** Daily availabilities for a date range. */
  async getAvailabilities(
    hotelCode: string,
    params: PmsDateRangeQuery
  ): Promise<PmsAvailability[]> {
    const query = this.buildQuery({
      from: params.from,
      to: params.to,
      room_type_ids: params.roomTypeIds,
      page: params.page,
      pageSize: params.pageSize,
    });
    const data = await this.request<
      PmsAvailability[] | { availabilities?: PmsAvailability[] }
    >(`/v1/availabilities${query}`, hotelCode);
    return Array.isArray(data) ? data : (data?.availabilities ?? []);
  }
}

export const pmsInfoService = new PmsInfoService();
